import { GQL } from '@aline/graphql';
import { produce } from 'immer';
import { last } from 'ramda';
import type { Descendant, EditorNodesOptions, Location, NodeEntry, Point, Range } from 'slate';
import { Editor, Element, Node, Path, Scrubber, Span, Text } from 'slate';

import type { UUID } from '../../types/type-helpers';
import { isUUIDV4 } from '../../util';
import { scrubber } from '../../util/scrubber';
import type {
  AlineContentBlock,
  AlineDescendant,
  AlineNode,
  DocumentElement,
  FakeElement,
  FooterElement,
  HeaderElement,
  ParagraphElement,
  SelBoundaryNode,
  TabElement,
} from '../internal';
import {
  breakType,
  checkboxType,
  drawingType,
  FooterType,
  HeaderType,
  inputFieldType,
  isInputFieldElement,
  isMarkerElement,
  isPageNumberingElement,
  isParagraphElement,
  isTabElement,
  nodeIsSelBoundary,
  Queries,
  tabType,
} from '../internal';

export enum AlineTrackedChangeType {
  CheckboxEdit,
  TableEdit,
  TabEdit,
  DocumentEdit,
  ParagraphEdit,
  ParagraphMarkEdit,
  HeadersEdit,
  FootersEdit,
  FieldEdit,
  LinkEdit,
  BreakEdit,
  InsertText,
  RemoveText,
  FormatText,
  DrawingEdit,
  InstructionFieldEdit,
}

/** Tell Slate to use this scrubber for its error messages. */
Scrubber.setScrubber(scrubber);

const generalQueries = {
  absolutePoint(relativePoint: Point, basePath: Path): Point {
    return { ...relativePoint, path: basePath.concat(relativePoint.path) };
  },

  absoluteRange(relativeRange: Range, basePath: Path): Range {
    return {
      anchor: Queries.absolutePoint(relativeRange.anchor, basePath),
      focus: Queries.absolutePoint(relativeRange.focus, basePath),
    };
  },

  /**
   * Returns a boolean indicating whether an element has no children along the first or last branch.
   */
  branchEmpty: (element: Element, options: { branch?: 'first' | 'last' } = {}): boolean => {
    const { branch = 'last' } = options;
    const { children } = element;

    if (children.length === 0) return true;

    const [firstChild] = children;
    const lastChild = children[children.length - 1];
    const child = branch === 'first' ? firstChild : lastChild;

    if (Text.isText(child)) return false;

    return Queries.branchEmpty(child, { branch });
  },

  /**
   * Returns the path to the tip of a branch inside a rootElement.
   */
  branchTipPath: (rootElement: Element, options: { tip?: 'first' | 'last' } = {}): Path => {
    const { tip = 'last' } = options;
    const { children } = rootElement;

    const index = tip === 'first' ? 0 : children.length - 1;

    const child = children[index];

    if (Text.isText(child) || child.children.length === 0) return [index];

    return [index, ...Queries.branchTipPath(child, { tip })];
  },

  /**
   * Returns whether a node has a nested page number that is different for each page.
   */
  containsPageNumbers: (editor: Editor, at?: Location): boolean => {
    const nodeEntries = Editor.nodes(editor, {
      at,
      match: isPageNumberingElement,
      voids: true,
    });
    return Boolean(nodeEntries.next().value);
  },

  descendantProps<T extends AlineDescendant>(descendant: T) {
    if (Text.isText(descendant)) {
      const { text, ...props } = descendant;
      return props;
    }

    const { children, ...props } = descendant;
    return props;
  },

  /**
   * Get a sliced fragment of the content node(s) present at the selection.
   */
  fragmentAtLocation: (
    editor: Editor,
    options: { at?: Location | null } = {}
  ): AlineContentBlock[] | ParagraphElement['children'] => {
    const { at = editor.selection } = options;
    if (!at) return [];

    const fieldEntryAbove = Editor.above(editor, {
      at,
      match: isInputFieldElement,
      voids: true,
    });
    // If the selection is inside an input field, we want to copy the field.
    const locationRange = fieldEntryAbove
      ? Editor.range(editor, fieldEntryAbove[1])
      : Editor.range(editor, at);
    const rootEntry = Editor.above<SelBoundaryNode | ParagraphElement>(editor, {
      at: locationRange,
      match: node => {
        const selectionAcrossParagraphs = Queries.rangeAcrossParagraphs(editor);
        if (!selectionAcrossParagraphs) return isParagraphElement(node);

        return nodeIsSelBoundary(node);
      },
    });
    if (!rootEntry) return [];

    /**
     * In addition to getting info about markers in the range, this also extends the range to
     * include markers just before the start of the range, which also need to be included in the
     * fragment if they're paired.
     */
    const { adjustedRange, markerInfoMap } = Queries.getMarkersInRange(editor, locationRange);
    const [rootNode, rootPath] = rootEntry;
    const relRange = Queries.relativeRange(adjustedRange, rootPath);

    /**
     * Node.fragment has a return type of Descendant[] by default, but actually returns a fragment
     * with the same shape as the root node's children.
     *
     * TODO: Use generic type after this https://github.com/ianstormtaylor/slate/pull/5926 is released.
     */
    const fragment = Node.fragment(rootNode, relRange);
    // Remove any unpaired markers from the fragment...
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const fragmentRoot: FakeElement<any> = {
      children: fragment,
      suggestedEdits: [],
      type: 'fake',
    };
    const { children } = produce(fragmentRoot, draftNode => {
      const nodeEntries = Array.from(Node.nodes(draftNode, { reverse: true }));

      for (const [node, path] of nodeEntries) {
        if (isMarkerElement(node)) {
          const markerInfo = markerInfoMap.get(Queries.getMarkerKey(node));

          if (!markerInfo?.paired) {
            const parent = Node.parent(draftNode, path);
            const index = path[path.length - 1];
            parent.children.splice(index, 1);
          }
        }
      }
    });

    return children;
  },

  /**
   * Returns the active "root entries" (Document and active Header(s)/Footer(s)).
   *
   * The NodeEntry<DocumentElement> is guaranteed to be first.
   */
  getActiveRootEntries(
    editor: Editor
  ): [NodeEntry<DocumentElement>, ...NodeEntry<HeaderElement | FooterElement>[]] {
    const documentEntry = Queries.getDocument(editor);
    const headersEntry = Queries.getHeadersElement(editor);
    const footersEntry = Queries.getFootersElement(editor);
    if (!headersEntry || !footersEntry) return [documentEntry];

    const activeHeaderEntries = Queries.activeHeaderTypes(editor).map(type =>
      Queries.getHeaderOrFooter({ editor, partEntry: headersEntry, type })
    );
    const activeFooterEntries = Queries.activeFooterTypes(editor).map(type =>
      Queries.getHeaderOrFooter({ editor, partEntry: footersEntry, type })
    );

    return [documentEntry, ...activeHeaderEntries, ...activeFooterEntries];
  },

  /**
   * Returns the first non-positional tab element
   */
  getFirstNotPositionalTabInDescendants(element: Element): TabElement | null {
    for (const child of element.children) {
      if (isTabElement(child) && !child.isPositional) return child;
      if (Element.isElement(child) && child.children?.length) {
        const found = Queries.getFirstNotPositionalTabInDescendants(child);
        if (found) return found;
      }
    }
    return null;
  },

  /**
   * Returns a list of unique tracked change types that are in a given document.
   */
  getTrackedChangeTypes: (editor: Editor): AlineTrackedChangeType[] => {
    const rootEntries = Queries.getActiveRootEntries(editor);
    const nodeEntriesInDoc = rootEntries.flatMap(([, path]) => [
      ...Editor.nodes<AlineNode>(editor, { at: path, voids: true }),
    ]);
    const activeChangeTypesSet = new Set(
      nodeEntriesInDoc.flatMap(([node]): AlineTrackedChangeType[] => {
        if (Editor.isEditor(node)) return [];

        if (!Element.isElement(node)) {
          return (
            node.suggestedEdits?.map((edit): AlineTrackedChangeType => {
              switch (edit.type) {
                case 'insertNode':
                  return AlineTrackedChangeType.InsertText;
                case 'deleteNode':
                  return AlineTrackedChangeType.RemoveText;
                case 'textFormat':
                  return AlineTrackedChangeType.FormatText;
              }
            }) ?? []
          );
        }

        switch (node.type) {
          case 'fake':
          case 'list':
          case 'bookmarkStart':
          case 'bookmarkEnd':
          case 'suggestionMarkerStart':
          case 'suggestionMarkerEnd':
            return [];
          case drawingType:
            return node.suggestedEdits?.length ? [AlineTrackedChangeType.DrawingEdit] : [];
          case 'break':
            return node.suggestedEdits?.length ? [AlineTrackedChangeType.BreakEdit] : [];
          case 'document':
            return node.suggestedEdits?.length ? [AlineTrackedChangeType.DocumentEdit] : [];
          case 'headers':
          case HeaderType.Default:
          case HeaderType.Even:
          case HeaderType.FirstPage:
            return node.suggestedEdits?.length ? [AlineTrackedChangeType.HeadersEdit] : [];
          case 'footers':
          case FooterType.Default:
          case FooterType.Even:
          case FooterType.FirstPage:
            return node.suggestedEdits?.length ? [AlineTrackedChangeType.FootersEdit] : [];
          case 'table':
          case 'table-row':
          case 'table-cell':
            return node.suggestedEdits?.length ? [AlineTrackedChangeType.TableEdit] : [];
          case 'paragraph': {
            const paragraphTypes: AlineTrackedChangeType[] = [];
            if (node.suggestedEdits?.length)
              paragraphTypes.push(AlineTrackedChangeType.ParagraphEdit);
            // Split/merge suggestions live on glyphProps, not node.suggestedEdits.
            if (node.glyphProps?.suggestedEdits?.length)
              paragraphTypes.push(AlineTrackedChangeType.ParagraphMarkEdit);
            return paragraphTypes;
          }
          case 'link':
            return node.suggestedEdits?.length ? [AlineTrackedChangeType.LinkEdit] : [];
          case 'dynamic-block':
          case 'dynamic-inline':
          case 'INPUT':
          case 'CANNED':
            return node.suggestedEdits?.length ? [AlineTrackedChangeType.FieldEdit] : [];
          case 'tab':
            return node.suggestedEdits?.length ? [AlineTrackedChangeType.TabEdit] : [];
          case 'checkbox':
            return node.suggestedEdits?.length ? [AlineTrackedChangeType.CheckboxEdit] : [];
          case 'instructionField':
            return node.suggestedEdits?.length ? [AlineTrackedChangeType.InstructionFieldEdit] : [];
        }
      })
    );

    return [...activeChangeTypesSet];
  },

  /**
   * Get all authors who have suggested edits in the doc.
   */
  getTrackedChangesAuthorInfo(editor: Editor): { authorIds: Set<UUID>; authorNames: Set<string> } {
    const nodes = [...Editor.nodes<AlineNode>(editor, { at: [], voids: true })];

    function collectAuthor(
      acc: { authorIds: Set<UUID>; authorNames: Set<string> },
      authorId: string
    ): void {
      if (isUUIDV4(authorId)) acc.authorIds.add(authorId);
      else acc.authorNames.add(authorId);
    }

    return nodes.reduce<{ authorIds: Set<UUID>; authorNames: Set<string> }>(
      (acc, [node]) => {
        if (Editor.isEditor(node)) return acc;

        node.suggestedEdits?.forEach(edit => collectAuthor(acc, edit.authorId));
        // Paragraph split/merge suggestions store their author on glyphProps, not node.suggestedEdits.
        if (isParagraphElement(node))
          node.glyphProps?.suggestedEdits?.forEach(edit => collectAuthor(acc, edit.authorId));
        return acc;
      },
      { authorIds: new Set<UUID>(), authorNames: new Set<string>() }
    );
  },

  getWordsAndSpaces: (text: string): string[] => {
    return text.match(/\S+|\s/g) || [];
  },

  /**
   * Returns whether a node has text children anywhere in its tree.
   */
  hasText: (node: Node): boolean => {
    if (Editor.isEditor(node) || Element.isElement(node)) {
      const { children } = node;
      const textChild = children.some((child: Descendant) => Text.isText(child));
      if (textChild) return true;

      for (const child of children) {
        const childHasText = Queries.hasText(child);
        if (childHasText) return true;
      }

      return false;
    }

    // We have a text node and return true.
    return !!node.text;
  },

  /** Check if a range is entirely contained within a given path */
  isRangeWithinPath(range: Range, path: Path) {
    return Path.isCommon(path, range.anchor.path) && Path.isCommon(path, range.focus.path);
  },

  /**
   * Get the next sibling for the given path, or null if the path indicates the
   * last sibling.
   */
  nextSibling: (editor: Editor, path: Path): NodeEntry<Node> | null => {
    const nextPath = Path.next(path);
    if (Node.has(editor, nextPath)) {
      return Editor.node(editor, nextPath);
    }
    return null;
  },

  /**
   * Returns a boolean indicating whether a node is empty.
   */
  nodeIsEmpty: (node: Editor | Element | Text): boolean => {
    if ('children' in node) {
      switch (node.type) {
        case breakType:
        case checkboxType:
        case drawingType:
        case tabType:
          return false;
        case inputFieldType: {
          /**
           * If the input field is a signature field, we consider it non-empty.
           * If it's not a signature field though, we still want to run the
           * check on its children because other field types might be empty
           * or not depending on children.
           */
          if (node.variant === GQL.FieldVariant.Signature) return false;
        }
      }

      if (node.children.length === 0) return true;

      for (const child of node.children) {
        const childEmpty = Queries.nodeIsEmpty(child);
        if (!childEmpty) return false;
      }

      return true;
    }

    return node.text === '';
  },

  /**
   * Get the string content of a node or list of nodes independently of the editor. Queries.string
   * can also be used to extract a string from a known location in the editor.
   */
  nodeString(
    nodeOrNodes: Node | AlineDescendant[],
    options: {
      paragraphSeparator?: string;
      pass?: (nodeEntry: NodeEntry) => boolean;
      relativeRange?: Range;
    }
  ): string {
    const { paragraphSeparator = '', pass, relativeRange } = options;

    if (Text.isText(nodeOrNodes)) return nodeOrNodes.text;

    const root: Node = Array.isArray(nodeOrNodes)
      ? { children: nodeOrNodes, suggestedEdits: [], type: 'fake' }
      : nodeOrNodes;

    const fragmentRoot: Node = relativeRange
      ? {
          children: Node.fragment(root, relativeRange) as AlineDescendant[],
          suggestedEdits: [],
          type: 'fake',
        }
      : root;

    let string = '';
    let encounteredParagraph = false;

    for (const [node] of Node.descendants(fragmentRoot, { pass })) {
      if (isParagraphElement(node)) {
        if (encounteredParagraph) string += paragraphSeparator;
        encounteredParagraph = true;
      }

      if (Text.isText(node)) string += node.text;
    }

    return string;
  },

  /**
   * Grid-aware version of Editor.nodes
   */
  *nodes<T extends Node>(
    editor: Editor,
    options: EditorNodesOptions<T> = {}
  ): Generator<NodeEntry<T>, void, undefined> {
    if (options.universal) throw new Error('Queries.nodes does not support the universal option');

    const { at = editor.selection, mode = 'all' } = options;
    if (!at) return;

    const atIsGridSelection =
      !Span.isSpan(at) && !!Queries.getGridSelectionInfoForLocation(editor, { at });
    if (!atIsGridSelection) {
      yield* Editor.nodes<T>(editor, { ...options, at });
      return;
    }

    let hit: NodeEntry<T> | undefined;

    for (const [node, path] of gridSelectionNodes(editor, options)) {
      const isLower = hit !== undefined && Path.compare(path, hit[1]) === 0;

      // In highest mode any node lower than the last hit is not a match.
      if (mode === 'highest' && isLower) continue;

      // If there's a match and it's lower than the last, update the hit.
      if (mode === 'lowest' && isLower) {
        hit = [node, path];
        continue;
      }

      // In lowest mode we emit the last hit, once it's guaranteed lowest.
      const emit: NodeEntry<T> | undefined = mode === 'lowest' ? hit : [node, path];
      if (emit) yield emit;

      hit = [node, path];
    }

    // Since lowest is always emitting one behind, catch up at the end.
    if (mode === 'lowest' && hit) yield hit;
  },

  /**
   * Determines whether a path is abutting a reference path. See path-is-abutting.test.tsx to
   * understand what is meant by abutting.
   */
  pathIsAbutting(editor: Editor, referencePath: Path, path: Path): boolean {
    const compareInt = Path.compare(path, referencePath);

    switch (compareInt) {
      case 0: {
        // path/referencePath are ancestor/descendant or vice versa.
        const [ancestorPath, descendantPath] =
          path.length >= referencePath.length ? [referencePath, path] : [path, referencePath];
        const relativePath = Path.relative(descendantPath, ancestorPath);

        // Tells us whether the descendant is at the start of the ancestor path.
        return relativePath.every(number => number === 0);
      }
      default: {
        // path is before or after referencePath.
        const maxLevels = Math.max(path.length, referencePath.length);
        const [beforePath, afterPath] =
          compareInt === 1 ? [referencePath, path] : [path, referencePath];
        const commonPath = Path.common(beforePath, afterPath);
        const divergingLevel = commonPath.length;

        return Array.from({ length: maxLevels }).every((_, level) => {
          const sliceIndex = level + 1;
          const beforePathAtLevel = beforePath.slice(0, sliceIndex);
          const afterPathAtLevel = afterPath.slice(0, sliceIndex);
          const beforePathIndex = beforePathAtLevel[beforePathAtLevel.length - 1];
          const afterPathIndex = afterPathAtLevel[afterPathAtLevel.length - 1];

          if (level < divergingLevel) return Path.equals(beforePathAtLevel, afterPathAtLevel);
          // We expect beforePath and afterPath to be abutting siblings if we're at divergingLevel.
          if (level === divergingLevel)
            return (
              Path.isSibling(beforePathAtLevel, afterPathAtLevel) &&
              Math.abs(beforePathIndex - afterPathIndex) === 1
            );

          const isBeforePathDeeperThanDivergingLevel =
            beforePathAtLevel.length - 1 > divergingLevel;
          if (isBeforePathDeeperThanDivergingLevel) {
            const parent = Node.parent(editor, beforePathAtLevel);
            const lastChildIndex = parent.children.length - 1;
            const isBeforePathAtEnd = beforePathIndex === lastChildIndex;
            if (!isBeforePathAtEnd) return false;
          }

          const isAfterPathDeeperThanDivergingLevel = afterPathAtLevel.length - 1 > divergingLevel;
          if (isAfterPathDeeperThanDivergingLevel) {
            const isAfterPathAtStart = afterPathIndex === 0;
            if (!isAfterPathAtStart) return false;
          }

          return true;
        });
      }
    }
  },

  /**
   * Check if a given path matches a location, using the same logic as Editor.nodes.
   */
  pathMatchesLocation(editor: Editor, path: Path, location: Location | Span): boolean {
    let [from, to] = Span.isSpan(location)
      ? location
      : [
          Editor.path(editor, location, { edge: 'start' }),
          Editor.path(editor, location, { edge: 'end' }),
        ];

    // Ensure that from comes before to
    if (Path.isAfter(from, to)) [from, to] = [to, from];

    const satisfiesFrom =
      Path.equals(from, path) ||
      Path.isAncestor(from, path) ||
      Path.isDescendant(from, path) ||
      Path.isBefore(from, path);

    const satisfiesTo =
      Path.equals(to, path) ||
      Path.isAncestor(to, path) ||
      Path.isDescendant(to, path) ||
      Path.isAfter(to, path);

    return satisfiesFrom && satisfiesTo;
  },

  /**
   * Get the previous sibling for the given path, or null if the path indicates
   * the first sibling.
   */
  previousSibling: (editor: Editor, path: Path): NodeEntry<Node> | null => {
    if (last(path) === 0) {
      return null;
    }
    const previousPath = Path.previous(path);
    return [Node.get(editor, previousPath), previousPath];
  },

  relativePoint(point: Point, ancestor: Path): Point {
    return { ...point, path: Path.relative(point.path, ancestor) };
  },

  relativeRange(range: Range, ancestor: Path): Range {
    return {
      anchor: Queries.relativePoint(range.anchor, ancestor),
      focus: Queries.relativePoint(range.focus, ancestor),
    };
  },

  /** Scrub potentially sensitive information from any serializable value. */
  scrub<T>(value: T): T {
    return JSON.parse(JSON.stringify(value, scrubber));
  },

  /**
   * Get the string content of a location.
   */
  string(
    editor: Editor,
    options: { at: Location; paragraphSeparator?: string; voids?: boolean }
  ): string {
    const { at, paragraphSeparator, voids = false } = options;

    let range: Range;
    try {
      range = Editor.range(editor, at);
    } catch (error) {
      /**
       * We sometimes encounter malformed elements that do not contain any text nodes. Since this is
       * not valid in Slate, some of Slate's built-in queries such as Editor.range can throw errors
       * when encountering them.
       */
      if (error instanceof Error && error.message.startsWith('Cannot get the start point')) {
        console.error('Malformed node passed to Queries.string');
        return '';
      }

      throw error;
    }

    return Queries.nodeString(editor, {
      paragraphSeparator,
      pass: voids ? undefined : ([node]) => Element.isElement(node) && Editor.isVoid(editor, node),
      relativeRange: range,
    });
  },
};

export default generalQueries;

/**
 * The grid-selection counterpart of the Node.nodes traversal Editor.nodes runs
 */
function* gridSelectionNodes<T extends Node>(
  editor: Editor,
  options: EditorNodesOptions<T>
): Generator<NodeEntry<T>, void, undefined> {
  const visitedAncestors = new Set<Node>();
  const cellEntries = Queries.getGridSelectionCellEntries(editor) ?? [];

  for (const [, cellPath] of options.reverse ? cellEntries.toReversed() : cellEntries) {
    for (const [node, path] of Editor.nodes<T>(editor, { ...options, at: cellPath, mode: 'all' })) {
      const isCellAncestor = path.length < cellPath.length;
      if (isCellAncestor) {
        if (visitedAncestors.has(node)) continue;
        visitedAncestors.add(node);
      }

      yield [node, path];
    }
  }
}
